import { z } from 'zod';
import { FunctionIdentity } from './webapp-function.dto';

// Even though the old detection service is not used anymore, we keep the enum here for backwards compatibility
export enum IssueTypes {
  endpoint_degradation_wow = 'endpoint_degradation_wow',
  endpoint_degradation_2do2d = 'endpoint_degradation_2do2d',
  endpoint_degradation_spike = 'endpoint_degradation_spike',
  endpoint_degradation_post_deployment = 'endpoint_degradation_post_deployment',
  kafka_topic_consume_degradation_wow = 'kafka_topic_consume_degradation_wow',
  kafka_topic_consume_degradation_2do2d = 'kafka_topic_consume_degradation_2do2d',
  kafka_topic_consume_degradation_post_deployment = 'kafka_topic_consume_degradation_post_deployment',
  // TODO add kafka_topic_consume_degradation_spike = 'kafka_topic_consume_degradation_spike', ?
  new_error = 'new_error',
  generic = 'generic',
}

export const IssueTypesZod = z.enum([
  IssueTypes.endpoint_degradation_wow,
  IssueTypes.endpoint_degradation_2do2d,
  IssueTypes.endpoint_degradation_spike,
  IssueTypes.endpoint_degradation_post_deployment,

  IssueTypes.kafka_topic_consume_degradation_wow,
  IssueTypes.kafka_topic_consume_degradation_2do2d,
  IssueTypes.kafka_topic_consume_degradation_post_deployment,

  IssueTypes.new_error,
  IssueTypes.generic,
]);

export const DetectionPayload = z.object({
  accountIds: z.array(z.string()).optional(),
  environmentNames: z.array(z.string()).optional(),
  type: IssueTypesZod,
  dryRun: z.boolean().optional(),
  params: z
    .object({
      accountId: z.string(),
      environmentId: z.number(),
      environmentName: z.string(),
      serviceId: z.number(),
      serviceName: z.string(),
      deploymentTime: z.string().datetime(),
      affectedEndpoints: z.array(z.number()).optional(),
    })
    .optional(),
});
export type DetectionPayload = z.infer<typeof DetectionPayload>;

export const DetectFlowDegradationPayload = z.object({
  accountIds: z.array(z.string()).optional(),
  type: z.enum([
    IssueTypes.endpoint_degradation_wow,
    IssueTypes.endpoint_degradation_2do2d,
    IssueTypes.endpoint_degradation_spike,
    IssueTypes.kafka_topic_consume_degradation_wow,
    IssueTypes.kafka_topic_consume_degradation_2do2d,
  ]),
  config: z
    .object({
      flowP90DurRatioThreshold: z.number(),
      flowInvocationsThreshold: z.number(),
      flowCurrP90DurationThreshold: z.number(),
      flowRatioSimilarityThreshold: z.number(),
      flowDiffPercentageThreshold: z.number(),
      excludedServices: z.array(z.number()),
      detectionTime: z.string().datetime().optional(),
      skipSources: z.boolean().optional(),
    })
    .optional(),
});
export type DetectFlowDegradationPayload = z.infer<
  typeof DetectFlowDegradationPayload
>;

export const FlowDegradationPostDeploymentConfig = z.object({
  accountId: z.string(),
  environmentId: z.number(),
  environmentName: z.string(),
  serviceId: z.number(),
  dryRun: z.boolean().optional(),
  config: z.object({
    type: IssueTypesZod,
    flowP90DurRatioThreshold: z.number(),
    flowInvocationsThreshold: z.number(),
    flowCurrP90DurationThreshold: z.number(),
    flowRatioSimilarityThreshold: z.number(),
    flowDiffPercentageThreshold: z.number(),
    excludedServices: z.array(z.number()),
  }),
  params: z.object({
    serviceId: z.number(),
    serviceName: z.string(),
    deploymentTime: z.string().datetime(),
  }),
});
export type FlowDegradationPostDeploymentConfig = z.infer<
  typeof FlowDegradationPostDeploymentConfig
>;

export const DetectedIssue = z.object({
  issueType: IssueTypesZod,
  detectedAt: z.date(),
  accountId: z.string(),
  environmentName: z.string(),
  functionId: z.number().nullable(),
  serviceId: z.number(),
  endpointId: z.number().nullable(),
  kafkaTopicId: z.number().nullable(),
  payload: z.unknown(),
  severity: z.union([
    z.literal('critical'),
    z.literal('high'),
    z.literal('medium'),
    z.literal('low'),
  ]),
});

export type DetectedIssue = z.infer<typeof DetectedIssue>;

export const DetectedIssuesPayload = z.object({
  issues: z.array(DetectedIssue),
});

export const GenericIssuesDetected = z.array(
  DetectedIssue.omit({
    detectedAt: true,
    issueType: true,
  }).extend({
    // Added for swagger reasons only
    payload: z.object({
      title: z.string(),
      shortTitle: z.string().nullable(),
      value: z.string(),
      dedupKey: z.string(),
      slackTitle: z.string(),
      slackMessage: z.string(),
      sources: z.array(
        z.object({ functionId: z.number(), message: z.string() }),
      ),
    }),
  }),
);
export type GenericIssuesDetected = z.infer<typeof GenericIssuesDetected>;

export type DetectedIssuesPayload = z.infer<typeof DetectedIssuesPayload>;

export const DurationDetails = z.object({
  previous: z.number(),
  current: z.number(),
  accountsFor: z.number().optional(),
});
export type DurationDetails = z.infer<typeof DurationDetails>;

export const EntityDetails = z.object({
  p90Duration: DurationDetails.optional(),
  averageDuration: DurationDetails.optional(),
  lastChangedAt: z.coerce.date().optional(),
  deploymentTime: z.coerce.date().optional(),
});

export type EntityDetails = z.infer<typeof EntityDetails>;

export const ValueIcon = z.union([
  z.literal('info'),
  z.literal('trend'),
  z.literal('flame'),
  z.literal('right-arrow'),
]);

export type ValueIcon = z.infer<typeof ValueIcon>;

export const IssueValue = z.object({
  icon: ValueIcon.default('info'),
  content: z.string(),
  label: z.string().default('Value'),
});

export type IssueValue = z.infer<typeof IssueValue>;

const EntityBase = z.object({
  id: z.number(),
  name: z.string(),
  details: EntityDetails,
  icon: z.string().optional(),
  values: z.array(IssueValue).optional(),
});

export const FunctionEntity = EntityBase.extend({
  type: z.literal('function'),
  signature: z.string(),
  filePath: z.string(),
  repoName: z.string(),
  identity: FunctionIdentity,
});
export type FunctionEntity = z.infer<typeof FunctionEntity>;

export const EndpointEntity = EntityBase.extend({
  type: z.literal('endpoint'),
  path: z.string(),
  methods: z.array(z.string()),
  isMuted: z.boolean().optional(),
  isDetectionExcluded: z.boolean().optional(),
});
export type EndpointEntity = z.infer<typeof EndpointEntity>;

export const KafkaTopicEntity = EntityBase.extend({
  type: z.literal('kafka_topic'),
  groupId: z.string().optional(), // not in use for now
  queuePullingType: z.string().optional(), // not in use for now
  isMuted: z.boolean().optional(), // will be used in the near future
  isDetectionExcluded: z.boolean().optional(), // will be used in the near future
});
export type KafkaTopicEntity = z.infer<typeof KafkaTopicEntity>;

export const Entity = z.union([
  FunctionEntity,
  EndpointEntity,
  KafkaTopicEntity,
]);

export type Entity = z.infer<typeof Entity>;

export function isFunctionEntity(entity: Entity): entity is FunctionEntity {
  return entity.type === 'function';
}

export function isEndpointEntity(entity: Entity): entity is EndpointEntity {
  return entity.type === 'endpoint';
}

export function isKafkaTopicEntity(entity: Entity): entity is KafkaTopicEntity {
  return entity.type === 'kafka_topic';
}

export const Issue = z.object({
  id: z.number(),
  serviceId: z.number(),
  serviceName: z.string(),
  type: IssueTypesZod,
  title: z.string(),
  detectionTime: z.coerce.date(),
  shortTitle: z.string().nullable(),
  genericPayload: z
    .object({
      slackTitle: z.string(),
      slackMessage: z.string(),
      sources: z.array(
        z.object({
          functionId: z.number(),
          message: z.string(),
        }),
      ),
    })
    .optional(),
  value: z.string().nullable(),
  mainEntity: Entity,
  sources: z.array(Entity),
});

export type Issue = z.infer<typeof Issue>;

// This type is used only for the controller, to avoid sending the genericPayload to the client
// (Used both in '/issues' and '/issues/{issueId}' endpoints)
export const PublicIssue = Issue.omit({
  genericPayload: true,
});
export type PublicIssue = z.infer<typeof PublicIssue>;

export const IssuesResponse = z.object({
  issues: z.array(PublicIssue),
});

export type IssuesResponse = z.infer<typeof IssuesResponse>;

export enum EndpointSettingsAction {
  MUTE = 'mute',
  UNMUTE = 'unmute',
  EXCLUDE_DETECTION = 'exclude-detection',
  INCLUDE_DETECTION = 'include-detection',
}

const EndpointSettingsActionZod = z.enum([
  EndpointSettingsAction.MUTE,
  EndpointSettingsAction.UNMUTE,
  EndpointSettingsAction.EXCLUDE_DETECTION,
  EndpointSettingsAction.INCLUDE_DETECTION,
]);

export const UpdateEndpointSettingsPayload = z.object({
  environmentName: z.string(),
  endpointId: z.number(),
  serviceId: z.number(),
  action: EndpointSettingsActionZod,
});

export type UpdateEndpointSettingsPayload = z.infer<
  typeof UpdateEndpointSettingsPayload
>;

// get settings
export const GetEndpointsSettingsQuery = z.object({
  environmentName: z.string().optional(),
});
export type GetEndpointsSettingsQuery = z.infer<
  typeof GetEndpointsSettingsQuery
>;

export const EndpointsSettings = z.object({
  serviceId: z.number(),
  serviceName: z.string(),
  endpointId: z.number(),
  endpointPath: z.string(),
  isMuted: z.boolean(),
  isDetectionExcluded: z.boolean(),
});
export type EndpointsSettings = z.infer<typeof EndpointsSettings>;

export const EndpointsSettingsResponse = z.array(EndpointsSettings);
export type EndpointsSettingsResponse = z.infer<
  typeof EndpointsSettingsResponse
>;

export type UpdateEndpointSettingsResponse = { message: string };
